import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { aboutInfo } from '@/mocks/articles';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d95na52h9roq6qfrc7pg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('website_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }
    formData.delete('website_alt');

    try {
      setFormError('');
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        urlEncoded.append(key, value as string);
      });

      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed?.message as string) || responseText;

      if (response.ok && (parsed as Record<string, string>)?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        'name': '黑料六点半',
        'url': 'https://www.heilia630.com/',
        'description': aboutInfo.intro,
        'foundingDate': '2020',
        'contactPoint': {
          '@type': 'ContactPoint',
          'email': aboutInfo.contact.email,
          'contactType': 'customer service',
        },
      },
      {
        '@type': 'WebPage',
        'name': '关于我们 - 黑料六点半',
        'description': aboutInfo.intro,
        'url': 'https://www.heilia630.com/about',
        'about': {
          '@type': 'Organization',
          'name': '黑料六点半',
        },
      },
      {
        '@type': 'BreadcrumbList',
        'itemListElement': [
          { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://www.heilia630.com/' },
          { '@type': 'ListItem', 'position': 2, 'name': '关于我们' },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 黑料六点半"
        description={aboutInfo.intro}
        keywords="黑料六点半,51黑料,关于我们,品牌介绍,联系方式,投稿爆料"
        canonicalUrl="/about"
        ogType="website"
        schema={aboutSchema}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          {/* Hero */}
          <div className="relative h-[350px] md:h-[450px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Dark%20editorial%20office%20with%20warm%20desk%20lamps%2C%20journalism%20atmosphere%2C%20vintage%20newspaper%20clippings%20on%20wall%2C%20professional%20yet%20mysterious%20mood%2C%20film%20noir%20aesthetic%2C%20warm%20amber%20lighting%2C%20cinematic%20composition&width=1800&height=700&seq=about-hero-2026&orientation=landscape"
              alt="关于黑料六点半"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/65"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center px-4">
                <h1 className="text-4xl md:text-6xl font-heading font-black text-background-50 mb-4">
                  关于<span className="text-accent-400">我们</span>
                </h1>
                <p className="text-sm md:text-base text-background-50/75 max-w-lg mx-auto">
                  {aboutInfo.tagline}
                </p>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />

            {/* Brand Introduction */}
            <section className="py-12 md:py-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <p className="text-sm font-medium text-accent-600 uppercase tracking-wider mb-3">品牌介绍</p>
                  <h2 className="text-3xl md:text-4xl font-heading font-black text-foreground-950 mb-6">
                    国内领先的<span className="text-primary-500">娱乐八卦</span>资讯平台
                  </h2>
                  <p className="text-base text-foreground-600 leading-relaxed mb-4">
                    {aboutInfo.intro}
                  </p>
                  <p className="text-base text-foreground-600 leading-relaxed">
                    {aboutInfo.mission}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {aboutInfo.stats.map((stat) => (
                    <div
                      key={stat.label}
                      className="p-6 rounded-2xl bg-background-100 border border-background-200/70"
                    >
                      <p className="text-3xl md:text-4xl font-heading font-black text-primary-500 mb-1">
                        {stat.value}
                      </p>
                      <p className="text-sm font-semibold text-foreground-800 mb-1">{stat.label}</p>
                      <p className="text-xs text-foreground-400">{stat.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Editor's Note */}
            <section className="py-12 md:py-16 border-t border-background-200/70">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
                <div className="lg:col-span-3">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-3 h-3 rounded-full bg-accent-500"></span>
                    <span className="text-sm font-medium text-accent-600 uppercase tracking-wider">主编的话</span>
                  </div>
                  <blockquote className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 leading-relaxed mb-6 italic">
                    "我们相信公众有权了解娱乐圈的真实面貌。八卦不仅是消遣，更是对行业乱象的监督。黑料六点半存在的意义，就是让每一个吃瓜群众都能看到真相。"
                  </blockquote>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-user-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">老六</p>
                      <p className="text-xs text-foreground-400 flex items-center gap-1">
                        <i className="ri-verified-badge-line text-accent-500"></i>
                        黑料六点半 · 主编
                      </p>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-2">
                  <div className="relative h-80 rounded-2xl overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20journalist%20silhouette%20in%20dark%20office%2C%20dramatic%20lighting%20from%20desk%20lamp%2C%20mysterious%20atmosphere%2C%20film%20noir%20style%2C%20editorial%20photography%2C%20black%20and%20white%20with%20warm%20accents&width=600&height=800&seq=about-editor-2026&orientation=portrait"
                      alt="主编肖像"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-12 md:py-16 border-t border-background-200/70">
              <div className="text-center mb-10">
                <p className="text-sm font-medium text-accent-600 uppercase tracking-wider mb-2">为什么选择我们</p>
                <h2 className="text-3xl md:text-4xl font-heading font-black text-foreground-950">
                  五大<span className="text-primary-500">核心优势</span>
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="md:col-span-1 p-6 rounded-2xl bg-background-100 border border-background-200/70">
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
                    <i className="ri-flashlight-line text-xl text-accent-600"></i>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-2">极速更新</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">24小时轮班监控，重大爆料5分钟内发布，让你永远快人一步吃到大瓜。</p>
                </div>
                <div className="p-6 rounded-2xl bg-background-100 border border-background-200/70">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mb-4">
                    <i className="ri-shield-check-line text-xl text-primary-600"></i>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-2">匿名保护</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">99.7%匿名保护率，加密通道传输，爆料人信息绝对安全。</p>
                </div>
                <div className="p-6 rounded-2xl bg-background-100 border border-background-200/70">
                  <div className="w-12 h-12 rounded-xl bg-secondary-100 flex items-center justify-center mb-4">
                    <i className="ri-database-2-line text-xl text-secondary-600"></i>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-2">海量档案</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">12,800+篇独家爆料，完整时间线梳理，历年大瓜一网打尽。</p>
                </div>
                <div className="p-6 rounded-2xl bg-background-100 border border-background-200/70">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mb-4">
                    <i className="ri-team-line text-xl text-primary-600"></i>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-2">专业团队</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">资深娱记+调查记者+法律顾问，确保每一条爆料的专业性与合规性。</p>
                </div>
                <div className="p-6 rounded-2xl bg-background-100 border border-background-200/70">
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
                    <i className="ri-heart-line text-xl text-accent-600"></i>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-2">吃瓜社区</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">500万+日活吃瓜群众，评论区就是你的快乐源泉，一起八卦一起嗨。</p>
                </div>
              </div>
            </section>

            {/* Contact Form */}
            <section className="py-12 md:py-16 border-t border-background-200/70">
              <div className="max-w-2xl mx-auto">
                <div className="text-center mb-10">
                  <p className="text-sm font-medium text-accent-600 uppercase tracking-wider mb-2">联系我们</p>
                  <h2 className="text-3xl md:text-4xl font-heading font-black text-foreground-950 mb-3">
                    投稿<span className="text-primary-500">爆料</span>
                  </h2>
                  <p className="text-sm text-foreground-500">
                    有料你就来！我们承诺对所有爆料人信息严格保密
                  </p>
                </div>

                <form
                  onSubmit={handleSubmit}
                  data-readdy-form=""
                  className="space-y-5"
                >
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">您的称呼</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的称呼（可使用化名）"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">电子邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的电子邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="topic" className="block text-sm font-medium text-foreground-700 mb-1.5">爆料主题</label>
                    <input
                      type="text"
                      id="topic"
                      name="topic"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入爆料主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">爆料详情</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      placeholder="请详细描述您要爆料的内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                  </div>

                  <div className="honeypot-field">
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    提交爆料
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-accent-600 text-center font-medium">爆料已提交！我们会尽快核实处理，感谢您的贡献。</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center font-medium">{formError}</p>
                  )}
                </form>

                <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="text-center p-5 rounded-2xl bg-background-100 border border-background-200/70">
                    <div className="w-10 h-10 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-3">
                      <i className="ri-mail-line text-accent-600"></i>
                    </div>
                    <p className="text-xs text-foreground-400 mb-1">邮箱</p>
                    <p className="text-sm font-medium text-foreground-800">{aboutInfo.contact.email}</p>
                  </div>
                  <div className="text-center p-5 rounded-2xl bg-background-100 border border-background-200/70">
                    <div className="w-10 h-10 mx-auto rounded-full bg-primary-100 flex items-center justify-center mb-3">
                      <i className="ri-wechat-line text-primary-600"></i>
                    </div>
                    <p className="text-xs text-foreground-400 mb-1">微信</p>
                    <p className="text-sm font-medium text-foreground-800">{aboutInfo.contact.wechat}</p>
                  </div>
                  <div className="text-center p-5 rounded-2xl bg-background-100 border border-background-200/70">
                    <div className="w-10 h-10 mx-auto rounded-full bg-secondary-100 flex items-center justify-center mb-3">
                      <i className="ri-map-pin-line text-secondary-600"></i>
                    </div>
                    <p className="text-xs text-foreground-400 mb-1">地址</p>
                    <p className="text-sm font-medium text-foreground-800">{aboutInfo.contact.address}</p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}